/**
 * CLIENTS → ÉCHÉANCES & RELANCES — qui doit quoi au magasin, et depuis quand.
 *
 * POURQUOI CET ÉCRAN EXISTE. La caisse tient cet échéancier depuis toujours (§13.6 D) et le pousse
 * par son canal ; RIEN au back-office ne le lisait. Le siège ne savait donc pas qui devait de
 * l'argent. ⚠️ **Le manque n'était pas une surface d'ÉCRITURE** — une échéance se crée au comptoir,
 * avec le client en face, et c'est bien là qu'elle doit se créer — mais une surface de LECTURE
 * (décision utilisateur). Cet écran est donc DÉLIBÉRÉMENT en lecture seule.
 *
 * ⚠️ « EN RETARD » SE DÉRIVE, IL NE SE STOCKE PAS, et le schéma l'écrit lui-même. Une échéance
 * devient en retard sans que personne n'écrive rien : un drapeau posé hier serait faux aujourd'hui.
 * Le serveur le calcule à l'instant de la lecture.
 *
 * ⚠️ LE MOYEN ANNONCÉ N'EST PAS UN RÈGLEMENT. Le client a DIT qu'il paierait par chèque ; rien n'a
 * été encaissé. L'écran le nomme « annoncé » — le rendre comme un moyen de paiement ferait lire
 * « payé par chèque » là où il faut lire « a annoncé un chèque » (§23.0).
 *
 * ⚠️ ET LE NUMÉRO DE PIÈCE EST RECOPIÉ, JAMAIS UNE CLÉ : il informe, il n'ouvre rien. En faire un
 * lien promettrait une navigation qui n'existe pas.
 */
import { useCallback, useEffect, useState } from 'react';
import { EmptyState } from '@orgazone/ui-web';
import { reminderMethodText } from '@orgazone/sync-contract';
import { api, type EcheancierDuSiege, type RelanceDuSiege } from '../api';
import { dateCourte, euros } from './titres';
import { t } from '../i18n';

/** Le mot d'un moyen annoncé, traduit ; le code brut s'il est inconnu. */
function methodWord(code: string): string {
  const word = reminderMethodText(code);
  return word ? t(word) : code;
}

export function Relances() {
  const [jeu, setJeu] = useState<EcheancierDuSiege | null>(null);
  const [echec, setEchec] = useState<string | null>(null);

  const relire = useCallback(() => {
    api.relances()
      // ⚠️ L'ÉCHEC SE DIT : un échéancier vide se lirait « personne ne doit rien », et le siège
      //    cesserait de relancer — exactement le contraire de ce que cet écran sert à faire.
      .then((r) => { setJeu(r); setEchec(null); })
      .catch((e) => { setJeu(null); setEchec(e instanceof Error ? e.message : String(e)); });
  }, []);
  useEffect(relire, [relire]);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>
          {t('Échéances & relances')}
        </h1>
        <p style={{ margin: '3px 0 0', fontSize: '13.5px', color: 'var(--ink-500)' }}>
          {t('Ce que les clients ont annoncé devoir régler, tenu en caisse. Une échéance se crée au comptoir — ici, on la consulte.')}
        </p>
      </div>

      {echec ? (
        <p role="alert" style={{ margin: 0, padding: '18px 2px', color: 'var(--danger-ink)', fontSize: 'var(--t-body)', lineHeight: 1.6 }}>
          {t('L’échéancier n’a pas pu être lu : {failure}. Ce n’est pas « personne ne doit rien ».', { failure: echec })}
        </p>
      ) : jeu === null ? (
        <p style={{ margin: 0, padding: '18px 2px', color: 'var(--ink-500)', fontSize: 'var(--t-body)' }}>{t('Lecture de l’échéancier…')}</p>
      ) : jeu.rows.length === 0 ? (
        <EmptyState icon="calendar" title={t('Aucune échéance')}
          note={t('Personne n’a d’échéance en cours dans ce magasin. Elles se créent en caisse, quand un client annonce un règlement à venir.')} />
      ) : (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
            <Tuile titre={t('Reste à encaisser')} valeur={euros(jeu.dueCents)} teinte="var(--ink-900)" />
            {/* ⚠️ LE RETARD SE MONTRE À PART, et pas comme une part du total : c'est LUI qui décide
                s'il faut appeler quelqu'un aujourd'hui. Noyé dans le reste, il ne se voit plus. */}
            <Tuile titre={t('Dont en retard')} valeur={euros(jeu.overdueCents)}
              teinte={jeu.overdueCents > 0 ? 'var(--rose-fg)' : 'var(--ink-400)'} />
          </div>

          <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
            {jeu.rows.map((r) => <Ligne key={r.id} r={r} />)}
          </div>
        </>
      )}
    </div>
  );
}

function Tuile({ titre, valeur, teinte }: { titre: string; valeur: string; teinte: string }) {
  return (
    <div style={{ flex: 1, minWidth: 180, padding: '12px 15px', background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)' }}>
      <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{titre}</span>
      <span style={{ display: 'block', marginTop: 3, fontSize: 'var(--t-h3)', fontWeight: 700, letterSpacing: '-0.02em', color: teinte }}>{valeur}</span>
    </div>
  );
}

function Ligne({ r }: { r: RelanceDuSiege }) {
  const regle = r.status === 'done';
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '110px minmax(140px,1fr) 110px minmax(0,150px) minmax(0,160px)', gap: 12, padding: '11px 16px', alignItems: 'center', borderBottom: '1px solid var(--ink-100)' }}>
      <span style={{ fontSize: 'var(--t-dense)', fontWeight: r.overdue ? 700 : 600, color: r.overdue ? 'var(--rose-fg)' : 'var(--ink-700)', whiteSpace: 'nowrap' }}>
        {dateCourte(r.dueDate)}
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 'var(--t-lead)', fontWeight: 600, color: regle ? 'var(--ink-500)' : 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {/* ⚠️ « client non précisé » et non un tiret : une échéance sans client existe (un acompte
              de passage), et un tiret se lirait comme une donnée manquante. */}
          {r.clientName ?? t('client non précisé')}
        </span>
        {r.note ? (
          <span style={{ display: 'block', fontSize: 'var(--t-micro)', color: 'var(--ink-500)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.note}</span>
        ) : null}
      </span>
      <span style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: regle ? 'var(--ink-400)' : 'var(--ink-900)', whiteSpace: 'nowrap' }}>
        {euros(r.amountCents)}
      </span>
      <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {/* ⚠️ « annoncé » est le mot juste : rien n'a été encaissé. Le code est NOMMÉ par la table du contrat
            (la même que la caisse) ; un code inconnu reste affiché tel quel plutôt que deviné. */}
        {r.expectedMethod ? t('{v} annoncé', { v: methodWord(r.expectedMethod) }) : ''}
      </span>
      <span style={{ justifySelf: 'start' }}>
        {regle ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-600)', fontSize: 'var(--t-micro)', fontWeight: 700, whiteSpace: 'nowrap' }}>
            {t('réglé le {v}', { v: dateCourte(r.doneAt) })}
          </span>
        ) : r.overdue ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px', borderRadius: 'var(--r-pill)', background: 'var(--rose-bg)', color: 'var(--rose-fg)', fontSize: 'var(--t-micro)', fontWeight: 700, whiteSpace: 'nowrap' }}>
            {t('en retard')}
          </span>
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px', borderRadius: 'var(--r-pill)', background: 'var(--teal-100)', color: 'var(--teal-800)', fontSize: 'var(--t-micro)', fontWeight: 700, whiteSpace: 'nowrap' }}>
            {t('à venir')}
          </span>
        )}
      </span>
    </div>
  );
}
